/**
 * Le magasin de session, rendu disponible à l'arbre React.
 *
 * Un contexte, et non un magasin importé depuis un module : le magasin est
 * **construit** par `main.tsx` et par chaque test, et un module qui en
 * exporterait une instance ferait fuir l'état d'un test au suivant. C'est la
 * même raison qui a fait choisir une instance d'i18next par montage.
 *
 * Le contexte n'a **pas de valeur par défaut utilisable** : hors du
 * fournisseur, les crochets lèvent. Un magasin de repli créé à la volée
 * paraîtrait fonctionner et perdrait chaque écriture au rendu suivant — un
 * défaut qui ne se voit qu'en production, sur une connexion qui « ne fait
 * rien ».
 */

import { createContext, useContext } from 'react';
import type { ReactNode } from 'react';

import { useStore } from 'zustand';

import type { SessionState, SessionStore } from './session-store.js';

const SessionContext = createContext<SessionStore | null>(null);

/** Rend [store] disponible aux composants de [children]. */
export function SessionProvider({
  store,
  children,
}: {
  readonly store: SessionStore;
  readonly children: ReactNode;
}): ReactNode {
  return <SessionContext.Provider value={store}>{children}</SessionContext.Provider>;
}

/** Le magasin du contexte. Lève hors du fournisseur. */
export function useSessionStore(): SessionStore {
  const store = useContext(SessionContext);
  if (store === null) {
    throw new Error(
      "ÉCHEC : aucun magasin de session dans le contexte. L'arbre doit être " +
        'enveloppé par <SessionProvider>. Un magasin de repli construit ici ' +
        'perdrait chaque écriture au rendu suivant, sans rien signaler.',
    );
  }
  return store;
}

/** Une part de l'état de session, sélectionnée et abonnée. */
export function useSession<T>(selector: (state: SessionState) => T): T {
  return useStore(useSessionStore(), selector);
}
